import { Check, Info, X } from 'lucide-react';
import { useState } from 'react';
import type { OptionGroup } from '~/lib/feature-modules/types';
import { cn } from '~/lib/utils';

interface Props {
  group: OptionGroup;
  selected: string | string[] | undefined;
  onChange?: (groupId: string, value: string | string[]) => void;
  readOnly?: boolean;
}

export default function OptionSelector({
  group,
  selected,
  onChange,
  readOnly = false,
}: Props) {
  const [expandedInfo, setExpandedInfo] = useState<string | null>(null);

  const selectedIds = selected
    ? Array.isArray(selected)
      ? selected
      : [selected]
    : [];

  function toggle(optionId: string) {
    if (readOnly || !onChange) return;
    if (group.type === 'single') {
      onChange(group.id, optionId);
    } else {
      const next = selectedIds.includes(optionId)
        ? selectedIds.filter((id) => id !== optionId)
        : [...selectedIds, optionId];
      onChange(group.id, next);
    }
  }

  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        {group.label}
        {group.required && <span className="text-red-400 ml-0.5">*</span>}
      </p>
      <div className="space-y-1.5">
        {group.options.map((opt) => {
          const isSelected = selectedIds.includes(opt.id);
          const isInfoOpen = expandedInfo === opt.id;
          const hasDeepInfo = !!(opt.whyChoose || opt.avoid);

          return (
            <div key={opt.id}>
              <button
                type="button"
                onClick={() => toggle(opt.id)}
                disabled={readOnly}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors',
                  isSelected
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
                  readOnly && 'cursor-default',
                )}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={cn(
                      'mt-0.5 flex-shrink-0 h-4 w-4 rounded flex items-center justify-center border',
                      group.type === 'single' ? 'rounded-full' : 'rounded',
                      isSelected
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-gray-300 bg-white',
                    )}
                  >
                    {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{opt.label}</span>
                      {hasDeepInfo && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedInfo(isInfoOpen ? null : opt.id);
                          }}
                          className={cn(
                            'flex-shrink-0 rounded-full p-0.5 transition-colors',
                            isInfoOpen
                              ? 'text-indigo-600 bg-indigo-100'
                              : isSelected
                                ? 'text-indigo-400 hover:text-indigo-600'
                                : 'text-gray-400 hover:text-gray-600',
                          )}
                          title="Why choose this?"
                        >
                          {isInfoOpen ? (
                            <X className="h-3 w-3" />
                          ) : (
                            <Info className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </div>
                    <span className="text-gray-500 text-xs">
                      {opt.description}
                    </span>
                  </div>
                </div>
              </button>

              {/* Why choose / avoid panel */}
              {isInfoOpen && hasDeepInfo && (
                <div className="mx-0.5 border border-t-0 border-indigo-200 rounded-b-lg bg-white px-3 py-2.5 space-y-2">
                  {opt.whyChoose && (
                    <div>
                      <p className="text-xs font-semibold text-indigo-700 mb-0.5">
                        When to pick this
                      </p>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {opt.whyChoose}
                      </p>
                    </div>
                  )}
                  {opt.avoid && (
                    <div>
                      <p className="text-xs font-semibold text-amber-700 mb-0.5">
                        When to avoid
                      </p>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {opt.avoid}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
